import type { getHomeVideoCTAs } from '@bulknutrients/bulk-shared-lib/lib/sanity/homePage';
import { Button } from '@components/Button/Button';
import { Video } from '@components/Media/Video/Video';
import { useWindowSize } from '@hooks/useWindowSize';
import { useEffect, useRef, useState } from 'react';

export function HomePageVideoCTA({
  homeVideoCTA,
}: {
  homeVideoCTA: Awaited<ReturnType<typeof getHomeVideoCTAs>>;
}) {
  const { width } = useWindowSize();
  const [videoSource, setVideoSource] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!width || !homeVideoCTA) return;

    // Set the sources based on the closed width match of the available videos
    const closestVideo = homeVideoCTA?.videos
      .reduce(
        (acc, video) => {
          acc.push({
            difference: Math.abs(video.width - width),
            playbackId: video.playbackId,
          });
          return acc;
        },
        [] as { difference: number; playbackId: string }[]
      )
      .sort((a, b) => a.difference - b.difference)[0];

    const playbackId = closestVideo
      ? closestVideo.playbackId
      : homeVideoCTA.videos.sort((a, b) => a.width - b.width)[0].playbackId;

    setVideoSource(playbackId);

    videoRef.current?.load();
  }, [width, homeVideoCTA]);

  return (
    <>
      <div className="mx-auto w-full max-w-[1536px]">
        <div className="relative h-[100vw] overflow-hidden lg:h-[calc(min(100vw,1536px)/3)] lg:rounded-2xl">
          <Video
            playbackId={videoSource}
            isBackgroundVideo
            altText={homeVideoCTA?.tagline}
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row justify-center items-center p-4 lg:p-6 xl:p-8 gap-4 flex-1">
        <h2 className="mx-0 font-primary font-semibold text-black text-2xl italic dark:text-white text-center sm:text-4xl lg:text-5xl xl:text-6xl">
          {homeVideoCTA?.tagline}
        </h2>
        <div>
          <Button
            className="home-cta-primary lg:py-2 lg:text-xl"
            href={homeVideoCTA?.slug}
            buttonStyle="primary"
            data-umami-event-location="homePageVideoCTA"
          >
            Shop Now
          </Button>
        </div>
      </div>
    </>
  );
}
